import React from 'react';
import {SparklesIcon} from '@heroicons/react/solid';

import Tooltip from './Tooltip';
import {SimpleCheckIcon, InformationIcon, ShieldIcon} from './svg';
import Button, {ButtonSizes, ButtonTypes} from './ButtonNew';
import {cx} from '../util';

export default function PlanCard(props) {
  const {
    className,
    highlighted = false,
    dark = false,
    name,
    features,
    price,
    priceSecondaryText,
    priceNote,
    discountedPrice,
    onClick,
    href,
    target,
    cta,
    primaryCta = false,
    disabled = false,
    badge,
    popularBadge,
    freeTrial,
    saleText,
    description,
  } = props;

  const classes = cx(
    'relative flex flex-col p-10 rounded-3xl max-w-xs shrink-0', {
      'bg-gray-900 border border-gray-900': dark,
      'bg-white border-gray-300': !dark,
      'border-2 border-indigo-700': highlighted && !dark,
      border: !highlighted && !dark,
    }, className,
  );

  let buttonType = ButtonTypes.SECONDARY;
  if (!dark && (highlighted || primaryCta)) buttonType = ButtonTypes.PRIMARY;

  let checkClasses = 'text-indigo';
  if (dark) {
    checkClasses = 'text-indigo-400';
  }

  return (
    <div className={classes} style={props.style}>
      {popularBadge && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 flex items-center gap-x-1.5 whitespace-nowrap rounded-full bg-indigo-600 px-3.5 py-1.5 text-xs font-semibold text-white">
          <SparklesIcon className="h-4 w-4" aria-hidden="true" />
          {popularBadge}
        </div>
      )}

      <div className="flex justify-between items-center">
        <div className={cx('text-xl font-semibold', dark ? 'text-white' : 'text-black')}>
          {name}
        </div>
        {badge && (
          <div
            className="flex items-center gap-x-1 text-xs font-medium uppercase rounded-xl px-3 py-1"
            style={{
              backgroundColor: badge.backgroundColor,
              color: badge.textColor,
            }}
          >
            {badge.icon && <badge.icon className="h-3.5 w-3.5" aria-hidden="true" />}
            {badge.text}
          </div>
        )}
      </div>

      <div className={cx('mt-4 text-sm mb-7 min-h-10', dark ? 'text-gray-300' : 'text-gray-600')}>
        {description}
      </div>

      {/* One wrapper so the price is a single row when cards share a subgrid. */}
      <div>
        {discountedPrice || freeTrial ? (
          <div className="-mt-5">
            <span className={cx('ml-1 text-sm line-through font-bold', dark ? 'text-green-400' : 'text-green-500')}>
              {price}
              &nbsp;
              {priceSecondaryText}
            </span>
            <div className="flex items-baseline gap-x-2">
              <span className={cx('text-4xl font-semibold tracking-tight', dark ? 'text-white' : 'text-gray-900')}>
                {discountedPrice}
              </span>
              <span className={cx('text-sm font-semibold', dark ? 'text-gray-400' : 'text-gray-600')}>
                {priceSecondaryText}
              </span>
              {freeTrial && <span className="text-4xl font-semibold text-green-400">Free</span>}
            </div>
          </div>
        ) : (
          <div className="flex items-baseline gap-x-2">
            <span className={cx('text-4xl font-semibold tracking-tight', dark ? 'text-white' : 'text-gray-900')}>{price}</span>
            <span className={cx('text-sm font-semibold', dark ? 'text-gray-400' : 'text-gray-600')}>{priceSecondaryText}</span>
          </div>
        )}

        {priceNote && (
          <div className={cx('mt-1 text-sm', dark ? 'text-gray-400' : 'text-gray-500')}>
            {priceNote}
          </div>
        )}
      </div>

      <Button
        buttonType={buttonType}
        size={ButtonSizes.LARGE}
        className="mt-6 text-base"
        onClick={onClick}
        href={href}
        target={target}
        disabled={disabled}
      >
        {cta}
      </Button>

      <ul className={cx('mt-8 space-y-3 text-sm', dark ? 'text-gray-200' : 'text-gray-600')}>
        {features.map((f) => {
          // Keep the tooltip icon glued to the label's last word so it never
          // wraps onto a line of its own.
          const words = f.tooltip ? String(f.label).split(' ') : null;
          const lastWord = words ? words.pop() : null;

          return (
            <li className="flex items-start">
              <SimpleCheckIcon className={cx('h-5 w-5 mr-3 shrink-0', checkClasses)} />
              {f.tooltip ? (
                <div>
                  {words.length > 0 && `${words.join(' ')} `}
                  <span className="whitespace-nowrap">
                    {lastWord}
                    <Tooltip content={f.tooltip} placement="top">
                      <span className="inline-block align-text-bottom">
                        <InformationIcon
                          className="cursor-pointer mx-1 h-4 w-4"
                          fill="#999999"
                        />
                      </span>
                    </Tooltip>
                  </span>
                </div>
              ) : (
                <div>{f.label}</div>
              )}
            </li>
          );
        })}
      </ul>
      {saleText && (
        <span className="mt-2 flex items-center justify-center font-medium text-gray-500 text-sm">
          <ShieldIcon className="mr-1 h-5 w-5" stroke="#888888" />
          {saleText}
        </span>
      )}
    </div>
  );
}
